import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, Heart, Music2, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VotingBallot — guest-facing "Vibe Democracy" UI shown when the venue has
 * flipped their session into voting mode. Guests see every track in the
 * active playlist, sorted by vote count desc (crowd favourites float to
 * the top). Tap a heart to vote. Each guest gets 3 votes per session.
 *
 * Feb 2026 — MVP scope: no auto-inject of top-voted track (DJ decides when
 * to play the winner). Real-time-ish via a 6-second poll (cheap for MVP;
 * WebSocket upgrade path is item 3 of the Master Control Center rollout).
 */
const VotingBallot = ({ code, session }) => {
  const [tracks, setTracks] = useState([]);
  const [playlistName, setPlaylistName] = useState('');
  const [myVoteCount, setMyVoteCount] = useState(0);
  const [maxVotes, setMaxVotes] = useState(3);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null); // track_id currently being voted for
  const pollTimer = useRef(null);

  const fetchBallot = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/requests/sessions/${code}/ballot`);
      if (r.data?.voting_mode) {
        setTracks(r.data.tracks || []);
        setPlaylistName(r.data.playlist_name || '');
        setMyVoteCount(r.data.my_vote_count || 0);
        setMaxVotes(r.data.max_votes_per_guest || 3);
      } else {
        setTracks([]);
      }
    } catch (err) {
      console.warn('[VotingBallot] fetch failed', err?.message);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    fetchBallot();
    pollTimer.current = setInterval(fetchBallot, 6000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [fetchBallot]);

  const castVote = async (trackId) => {
    if (voting) return; // debounce concurrent taps
    if (myVoteCount >= maxVotes) {
      toast.error(`You've used all ${maxVotes} votes for this session.`);
      return;
    }
    setVoting(trackId);
    try {
      const r = await axios.post(`${API}/requests/sessions/${code}/vote`, { track_id: trackId });
      if (r.data?.already_voted) {
        toast.info('You already voted for this one.');
      } else {
        toast.success('Vote counted!');
      }
      // Optimistic bump — server call will reconcile on next poll.
      setTracks((prev) => prev.map((t) => (
        t.id === trackId ? { ...t, votes: r.data.votes, voted_by_me: true } : t
      )));
      if (!r.data?.already_voted) setMyVoteCount((n) => Math.min(n + 1, maxVotes));
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save vote. Try again.';
      toast.error(msg);
    } finally {
      setVoting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="voting-ballot-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  if (!tracks.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center text-white/70" data-testid="voting-ballot-empty">
        <Music2 className="w-10 h-10 mb-3 opacity-40" />
        <p className="font-['Outfit'] font-semibold text-lg">Ballot loading…</p>
        <p className="text-sm mt-1">The DJ hasn't chosen a playlist yet.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="voting-ballot">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-[#FF007F]" />
          <span className="text-[11px] uppercase tracking-widest text-[#FF007F] font-bold font-['JetBrains_Mono']">
            Vibe Democracy
          </span>
        </div>
        <h1 className="text-white text-3xl font-black font-['Outfit'] leading-tight">
          Vote for what plays next
        </h1>
        {playlistName && (
          <p className="text-white/60 text-sm mt-1">
            From <span className="text-white/90">{playlistName}</span>
          </p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-white/50 text-xs font-['JetBrains_Mono']">
            {myVoteCount} of {maxVotes} votes used
          </span>
          <span className="text-white/40 text-xs">Top voted plays next</span>
        </div>
      </div>

      {/* Ballot list */}
      <ul className="space-y-2">
        {tracks.map((t, idx) => {
          const isVoting = voting === t.id;
          const disabled = isVoting || t.voted_by_me || myVoteCount >= maxVotes;
          const isLeader = idx === 0 && t.votes > 0;
          return (
            <li
              key={t.id}
              className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${
                isLeader
                  ? 'bg-gradient-to-r from-[#FF007F]/15 to-transparent border-[#FF007F]/40'
                  : 'bg-white/[0.03] border-white/5 hover:border-white/15'
              }`}
              data-testid={`ballot-row-${t.id}`}
            >
              {/* Rank */}
              <div className="w-6 flex-shrink-0 text-center">
                <span className={`text-xs font-black font-['JetBrains_Mono'] ${
                  isLeader ? 'text-[#FF007F]' : 'text-white/40'
                }`}>
                  #{idx + 1}
                </span>
              </div>
              {/* Art */}
              {t.album_image ? (
                <img
                  src={t.album_image}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Music2 className="w-5 h-5 text-white/40" />
                </div>
              )}
              {/* Meta */}
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-semibold truncate font-['Outfit']">
                  {t.name}
                </p>
                <p className="text-white/50 text-xs truncate">
                  {(t.artists || []).join(', ')}
                </p>
              </div>
              {/* Vote count + button */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-sm font-bold font-['JetBrains_Mono'] min-w-[24px] text-right ${
                    isLeader ? 'text-[#FF007F]' : 'text-white/70'
                  }`}
                  data-testid={`ballot-count-${t.id}`}
                >
                  {t.votes}
                </span>
                <button
                  type="button"
                  onClick={() => castVote(t.id)}
                  disabled={disabled}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    t.voted_by_me
                      ? 'bg-[#FF007F] text-white'
                      : disabled
                        ? 'bg-white/5 text-white/30'
                        : 'bg-white/10 text-[#FF007F] hover:bg-[#FF007F]/20 hover:scale-110 active:scale-95'
                  }`}
                  data-testid={`ballot-vote-${t.id}`}
                  aria-label={t.voted_by_me ? 'You voted for this' : 'Vote for this track'}
                >
                  {isVoting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Heart
                      className={`w-4 h-4 ${t.voted_by_me ? 'fill-current' : ''}`}
                    />
                  )}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {myVoteCount >= maxVotes && (
        <div
          className="mt-6 p-3 rounded-xl bg-white/5 border border-white/10 text-center"
          data-testid="ballot-cap-notice"
        >
          <p className="text-white/70 text-sm">
            All {maxVotes} votes used — thanks for voting! Watch the leader climb.
          </p>
        </div>
      )}
    </div>
  );
};

export default VotingBallot;
